import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import { useForm } from 'react-hook-form';
import axios from 'axios';

import {
  getFavouritesList,
  addToFavourites,
  deleteFromFavourites,
} from '../shared/store/authSlice.js';
import Loader from '../shared/displayComponents/loader/Loader';
import { createQueryString } from '../shared/utils/utils';
import { toasterOn } from '../shared/store/actions';
import url from '../constants/config';
import Player from './player/Player';
import FilterBar from './filterBar/FilterBar';
import RecentlyViewed from './recentlyViewed/RecentlyViewed';
import SavedSearches from './savedSearches/SavedSearches';
import SaveSearchModal from './savedSearches/SaveSearchModal';
import DeletePlayerModal from '../shared/displayComponents/deletePlayerModal/DeletePlayerModal';
import {
  fetchPlayers,
  addFilters,
  resetFilters,
  fetchFavs,
  resetPagination,
  deletePlayer,
} from './store/actions';
import './Players.scss';

const Players = () => {
  const { players, currentPage, totalCount, pageSize, selectedFilters } = useSelector(
    // eslint-disable-next-line no-shadow
    ({ players }) => players,
  );
  const { isLoading } = useSelector(({ shared }) => shared);

  const { userEmail, favouritesPlayers, userRole } = useSelector(
    ({ authorization }) => authorization,
  );

  const history = useHistory();

  const { handleSubmit, register, watch, reset } = useForm({
    defaultValues: {
      selectedFilters,
    },
  });
  const pathname = window.location.pathname;

  // ── Save Search modal state ──────────────────────────────────
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [savedSearchKey, setSavedSearchKey] = useState(0); // bump to re-fetch widget

  // ── Delete player modal state (lifted up to survive player unmount) ──
  const [deleteTarget, setDeleteTarget] = useState(null);

  const changeFavouriteStatus = async data => {
    if (favouritesPlayers.includes(data)) {
      await dispatch(deleteFromFavourites(userEmail, data));
    } else {
      await dispatch(addToFavourites(userEmail, data));
    }
    // Re-fetch player list on favorites page so removed cards disappear immediately
    if (pathname === '/favorite-players') {
      const queryString = createQueryString({ page: currentPage, ...filters });
      dispatch(fetchFavs(queryString, userEmail));
    }
  };

  const filters = watch();

  const SubmitFilter = values => {
    dispatch(addFilters(values));
    dispatch(resetPagination());
    // useEffect will handle the fetch when selectedFilters changes
  };

  const ResetForm = () => {
    dispatch(resetFilters());
    dispatch(resetPagination());
    // useEffect will handle the fetch when selectedFilters changes
  };

  const handleSaveSearch = async (name, filtersSnapshot) => {
    const token = window.localStorage.getItem('authorization');
    await axios.post(
      `${url}/coach/saved-searches`,
      { name, filters: filtersSnapshot },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    dispatch(toasterOn({ message: 'Search saved!', type: 'success' }));
    // Bump key so the SavedSearches widget refetches
    setSavedSearchKey(prev => prev + 1);
  };

  const handlePageClick = value => {
    // Scroll to top when changing page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    const queryString = createQueryString({
      page: value,
      ...filters,
    });
    pathname === '/available-players'
      ? dispatch(fetchPlayers(queryString))
      : dispatch(fetchFavs(queryString, userEmail));
  };

  const dispatch = useDispatch();

  useEffect(() => {
    const queryString = createQueryString({
      page: currentPage,
      ...selectedFilters,
    });
    if (pathname === '/available-players') {
      dispatch(fetchPlayers(queryString, userRole));
    } else if (userEmail) {
      dispatch(fetchFavs(queryString, userEmail));
    }
    reset(selectedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, reset, selectedFilters, pathname, userEmail]);

  useEffect(() => {
    dispatch(getFavouritesList(userEmail));
  }, [dispatch, userEmail]);

  // Backend already filters hidden players for non-admin roles
  const visiblePlayers = players;

  const isFavoritesPage = pathname === '/favorite-players';
  
  const isListEmpty = !visiblePlayers.length && !isLoading && (
    <div className="empty-state">
      <div className="empty-state__icon">
        {isFavoritesPage ? (
          <i className="far fa-heart" />
        ) : (
          <i className="fas fa-search" />
        )}
      </div>
      <h3 className="empty-state__title">
        {isFavoritesPage
          ? 'No favorites yet'
          : 'No players found'}
      </h3>
      <p className="empty-state__text">
        {isFavoritesPage
          ? "You haven't added any players to your favorites list yet. Browse available players and click the heart icon to add them here."
          : 'There is currently no match for your search. Please revise your filters and try again.'}
      </p>
      {isFavoritesPage && (
        <NavLink to="/available-players" className="empty-state__btn">
          <i className="fas fa-users" /> Browse Players
        </NavLink>
      )}
    </div>
  );

  const isCoachOrAdmin = userRole === 'coach' || userRole === 'admin';
  const isAvailablePlayers = pathname === '/available-players';
  const showSidebar = isAvailablePlayers && isCoachOrAdmin;

  return (
    <div className="Player">
      <FilterBar
        watch={watch}
        handleSubmit={handleSubmit}
        SubmitFilter={SubmitFilter}
        ResetForm={ResetForm}
        register={register}
        showSaveSearch={showSidebar}
        onSaveSearch={() => setIsSaveModalOpen(true)}
      />

      <div className={`players__layout ${showSidebar ? 'players__layout--with-sidebar' : ''}`}>
        {/* ── Main content: player grid ── */}
        <div className="players__main">
          {isListEmpty}
          {isLoading ? (
            <Loader />
          ) : (
            <div>
              <div className="players__columns">
                {visiblePlayers.map(player => (
                  <Player
                    key={player._id}
                    player={player}
                    changeFavouriteStatus={changeFavouriteStatus}
                    onDeletePlayer={id => setDeleteTarget(id)}
                  />
                ))}
              </div>
              <div className="Player__pagination">
                <Pagination
                  hideDisabled
                  activePage={currentPage}
                  itemsCountPerPage={pageSize}
                  totalItemsCount={totalCount}
                  pageRangeDisplayed={5}
                  onChange={handlePageClick}
                  itemClass="pagination__item"
                  linkClass="pagination__link"
                  activeClass="pagination__item--active"
                  firstPageText={<><i className="fas fa-angle-double-left" /> First</>}
                  lastPageText={<>Last <i className="fas fa-angle-double-right" /></>}
                  prevPageText={<i className="fas fa-chevron-left" />}
                  nextPageText={<i className="fas fa-chevron-right" />}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Sidebar: Recently Viewed + Saved Searches ── */}
        {showSidebar && (
          <aside className="players__sidebar">
            <RecentlyViewed />
            <SavedSearches key={savedSearchKey} />
          </aside>
        )}
      </div>

      {/* Save Search Modal */}
      {showSidebar && (
        <SaveSearchModal
          isOpen={isSaveModalOpen}
          onClose={() => setIsSaveModalOpen(false)}
          onSave={handleSaveSearch}
          filters={filters}
        />
      )}

      {/* Delete Player Confirmation */}
      <DeletePlayerModal
        showModal={!!deleteTarget}
        handleCloseModal={() => setDeleteTarget(null)}
        deletePlayerHandler={() => {
          if (deleteTarget) {
            dispatch(deletePlayer(deleteTarget, history));
          }
        }}
      />
    </div>
  );
};

export default Players;
